/**
 * When something happened, in the reader's own conventions.
 *
 * `undefined` as the locale is deliberate: it takes the browser's, which is the
 * one the operating system was set to. This application has one language and
 * dropped the picker, so there is nothing else to ask -- but a date is read by
 * where somebody is rather than by what they read in, and 3/4 and 4/3 are the
 * same day to two different people.
 *
 * The formatter is built once. `Intl.DateTimeFormat` is expensive to construct
 * and free to reuse, and a list of comments or history entries formats one date
 * per row.
 */
const WHEN = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

/** An ISO timestamp from the API, as a date and a time. */
export const whenText = (iso: string): string => WHEN.format(new Date(iso))

/**
 * The reader's own calendar day, as a key two instants can be compared by.
 *
 * Deliberately *not* `dayKey` from the schedule's calendar, which takes a
 * timezone because it answers a different question: a schedule buckets rows by
 * the zone its date column is pinned to, so that an event stays on the day it
 * was booked for wherever it is read. An activity heading is about when
 * something happened to the person reading it, which is their own zone and
 * nobody else's -- so there is no timezone to pass.
 */
const LOCAL_DAY = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function localDayKey(date: Date): string {
  const parts = LOCAL_DAY.formatToParts(date)
  const of = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return `${of('year')}-${of('month')}-${of('day')}`
}

/**
 * The day before one, by the calendar rather than by the clock.
 *
 * Subtracting a day from the *key* rather than 24 hours from the instant, which
 * is the whole reason this is not one line: the day a clock goes forward is 23
 * hours long, and "yesterday" would then be today for an hour of it.
 */
function previousDayKey(key: string): string {
  const [year, month, day] = key.split('-').map(Number)
  const at = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1))
  at.setUTCDate(at.getUTCDate() - 1)
  return `${String(at.getUTCFullYear()).padStart(4, '0')}-${String(
    at.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(at.getUTCDate()).padStart(2, '0')}`
}

const DAY = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' })

/**
 * A heading for the day something happened on, and the key to group by.
 *
 * The words are the caller's to translate and the date is not: this application
 * has one language, so "today" is an English word it owns, while the order the
 * parts of a date go in belongs to wherever the reader is -- the same split
 * `whenText` above makes.
 */
export type DayHeading =
  | { key: string; kind: 'today' }
  | { key: string; kind: 'yesterday' }
  | { key: string; kind: 'on'; text: string }

export function dayHeading(iso: string, now: Date): DayHeading {
  const at = new Date(iso)
  const key = localDayKey(at)
  const today = localDayKey(now)

  if (key === today) return { key, kind: 'today' }
  if (key === previousDayKey(today)) return { key, kind: 'yesterday' }
  return { key, kind: 'on', text: DAY.format(at) }
}
